import React from 'react'

import {
    StyledBaseGrid,
    StyledCell,
    StyledNestedGrid,
    StyledNestedCell,
    StyledFooterCell,
} from 'component-library/templates/BaseTheWest/BaseTheWest.styled'

import { TheWestStickySiteHeader } from 'component-library/content/TheWestStickySiteHeader/TheWestStickySiteHeader'
import { BaseTheWestProps } from './BaseTheWest'
import { navigation, quickLinks, getStaticNavInHouseLinks } from 'thewest/navigation'
import { TheWestHeader } from 'component-library/content/TheWestHeader/TheWestHeader'
import { FlyoutNav } from 'component-library/navigation/FlyoutNav/FlyoutNav'
import { TheWestHeaderActions } from 'component-library/navigation/TheWestHeaderActions/TheWestHeaderActions'
import { featurePublicationPageType } from 'thewest/routes/publication/kind/feature'
import { publicationPageType } from 'thewest/routes/publication/kind/new-article'

import { QuickLinks } from 'component-library/navigation/QuickLinks/QuickLinks'
import { Page } from 'page-lifecycle-provider'
import version from '../../../../thewest/src/server/version'
import { TheWestMainNavigation } from 'component-library/navigation/TheWestMainNavigation/TheWestMainNavigation'
import { TheWestFooterNetworkLinks } from 'component-library/navigation/TheWestFooterNetworkLinks/TheWestFooterNetworkLinks'
import { getFooterLinks } from 'thewest/footer'
import { TermsFooter } from 'component-library/navigation/TermsFooter/TermsFooter'
import { StyledStickyFooterAdUnit } from 'component-library/advertising/AdUnit/AdUnit.styled'
import { useSectionMeta } from 'common/routing/use-section-meta'
import { EntitledToPremium } from 'component-library/user-registration/entitlements/EntitledToPremium'
import { AdUnitWrapper } from 'component-library/advertising/AdUnit/AdUnitWrapper'
import { createIsEntitledSubscribeChangeHandler } from 'component-library/user-registration/subscribe/createIsEntitledSubscribeChangeHandler'
import { LazyTheWestLiveStickyBanner } from 'component-library/content/TheWestLive/TheWestLiveStickyBanner/TheWestLiveStickyBanner.lazy'
import { FeatureToggle } from 'component-library/feature-toggling/Toggle/Toggle'

export interface Props {
    headerAd?: (rendered?: () => void) => JSX.Element | undefined
    search?: JSX.Element
    header: JSX.Element
    primaryNav: JSX.Element
    flyoutNav: JSX.Element
    content: JSX.Element
    footerNav: JSX.Element
    footerNetworkLinks: JSX.Element
    footerTermsLinks: JSX.Element
    footerAd?: JSX.Element
    pageType: string
    // DPT-1375 extra Metadata for variant testing. Remove when finished
    pageProperties?: {
        isPremium: boolean
        publicationKind: 'article' | 'redirect'
        layout?: 'default' | 'feature'
    }
}

export const BaseTheWestWebTemplate: React.FC<Props> = props => {
    return (
        <StyledBaseGrid>
            <StyledCell>
                {props.flyoutNav}
                <TheWestStickySiteHeader
                    headerAd={props.headerAd && props.headerAd}
                    header={props.header}
                    navigation={props.primaryNav}
                />
                <StyledCell hasBackground={false}>{props.content}</StyledCell>
            </StyledCell>
            <StyledCell>{props.footerNav}</StyledCell>
            <StyledCell>
                <StyledNestedGrid>
                    <StyledNestedCell>{props.footerNetworkLinks}</StyledNestedCell>
                </StyledNestedGrid>
            </StyledCell>
            <StyledFooterCell hasBackground={true}>
                <StyledNestedGrid>
                    <StyledNestedCell>{props.footerTermsLinks}</StyledNestedCell>
                    {props.footerAd && <StyledNestedCell>{props.footerAd}</StyledNestedCell>}
                </StyledNestedGrid>
            </StyledFooterCell>
        </StyledBaseGrid>
    )
}

export const BaseTheWestWeb: React.FC<BaseTheWestProps> = ({
    additionalPageProperties,
    ads,
    onEvent,
    config,
    pageType,
    pageContents,
    section,
    flyOutProps,
    location,
}) => {
    /**
     * DPT-955 - Dont render quicklinks on feature publication page as we need to render it with a fill
     * so we will do that inside the routing for feature publication with quicklinks.routing.
     * DPT-1338 - we also don't want to render it on regional pages
     */

    const isArticlePage =
        pageType === featurePublicationPageType || pageType === publicationPageType
    const showQuickLinks = !isArticlePage && section === 'default'

    const sectionMeta = useSectionMeta()
    const footerLinks = getFooterLinks(config, sectionMeta)

    return (
        <React.Fragment>
            <EntitledToPremium
                // Used to track subscribe.complete events across the site, this differs from breachscreen.complete
                onIsEntitledChange={createIsEntitledSubscribeChangeHandler(location, onEvent)}
            />
            <FeatureToggle
                feature="the-west-live"
                on={() => {
                    if (!config.theWestLiveStationId) {
                        return null
                    }
                    return <LazyTheWestLiveStickyBanner showSponsorLogo={true} />
                }}
            />
            <BaseTheWestWebTemplate
                // DPT-1375 extra Metadata for variant testing. Remove when finished
                pageProperties={additionalPageProperties as any}
                pageType={pageType}
                headerAd={rendered =>
                    ads && (
                        <AdUnitWrapper
                            noticePosition="above-center"
                            padding={[4, 0, 0, 0]}
                            adState={ads}
                            unitId="header"
                            unitRendered={rendered}
                            onEvent={onEvent}
                        />
                    )
                }
                header={
                    <TheWestHeader
                        inHouseLinks={getStaticNavInHouseLinks(section)}
                        pageType={pageType}
                        onEvent={onEvent}
                    />
                }
                flyoutNav={
                    <FlyoutNav
                        navLinks={navigation}
                        section={section}
                        mainNavOpen={flyOutProps.mainNavOpen}
                        closeNavigation={flyOutProps.closeNavigation}
                        onEvent={onEvent}
                    />
                }
                primaryNav={
                    <TheWestHeaderActions
                        mainNavOpen={flyOutProps.mainNavOpen}
                        openNavigation={flyOutProps.openNavigation}
                        closeNavigation={flyOutProps.closeNavigation}
                        onEvent={onEvent}
                    />
                }
                content={
                    <React.Fragment>
                        {showQuickLinks && <QuickLinks links={quickLinks} onEvent={onEvent} />}
                        <Page
                            page={pageContents}
                            pageProperties={{
                                ...additionalPageProperties,
                                section,
                                pageType,
                                codebase: {
                                    version: version.buildNumber,
                                    sha: version.sha,
                                },
                            }}
                        />
                    </React.Fragment>
                }
                footerNav={
                    <TheWestMainNavigation
                        navLinks={navigation}
                        section={section}
                        onEvent={onEvent}
                        includeHome={true}
                        inHouseLinks={getStaticNavInHouseLinks(section)}
                        isFooterNav
                    />
                }
                footerNetworkLinks={
                    <TheWestFooterNetworkLinks
                        networkLinks={footerLinks.networkLinks}
                        socialLinks={footerLinks.socialLinks}
                    />
                }
                footerTermsLinks={
                    <TermsFooter
                        footerCompanyLinks={footerLinks.footerCompanyLinks}
                        footerTermsLinks={footerLinks.footerTermsLinks}
                    />
                }
                footerAd={
                    ads && (
                        <StyledStickyFooterAdUnit>
                            <AdUnitWrapper
                                noticePosition="none"
                                padding={[4, 0]}
                                adState={ads}
                                unitId="sticky-footer"
                                onEvent={onEvent}
                            />
                        </StyledStickyFooterAdUnit>
                    )
                }
            />
        </React.Fragment>
    )
}
